import React from 'react'
import { useTheme, Typography, Button, Box, Chip, Alert } from '@mui/material'
import { ExitToApp, Schedule, Info, Warning, FactCheck } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { FlexDiv } from '../FlexDiv'
import { AppRoutes } from '../../helpers/Routes'
import { FinalMoveOutStatus } from '../../containers/MoveOut/types'
import { HubMoveOutState } from '../../types/inspection'

interface MoveOutCardProps {
    status: FinalMoveOutStatus
    endDate: string
    leaseId: number
    moveOutState?: HubMoveOutState | null
    fmoAvailable?: boolean
}

const STATEMENT_STATUSES: FinalMoveOutStatus[] = [
    'fmo_sent',
    'fmo_reviewed',
    'fmo_accepted',
    'in_dispute',
    'final',
]

const getButtonConfig = (
    status: FinalMoveOutStatus,
    moveOutState?: HubMoveOutState | null,
    fmoAvailable?: boolean,
): { label: string; route: string; icon: React.ReactNode } => {
    if (fmoAvailable || STATEMENT_STATUSES.includes(status)) {
        return {
            label: 'View move-out statement',
            route: AppRoutes.moveOut,
            icon: <ExitToApp />,
        }
    }
    if (status === 'inspection_complete' || moveOutState === 'complete') {
        return {
            label: 'View inspection results',
            route: AppRoutes.inspectionMoveOut,
            icon: <FactCheck />,
        }
    }
    return {
        label: 'View move-out',
        route: AppRoutes.inspections,
        icon: <ExitToApp />,
    }
}

const getStatusColor = (status: FinalMoveOutStatus): string => {
    switch (status) {
        case 'final':
        case 'fmo_accepted':
            return '#10b981'
        case 'in_dispute':
            return '#ef4444'
        case 'fmo_sent':
        case 'fmo_reviewed':
            return '#6366f1'
        case 'inspection_complete':
            return '#3b82f6'
        case 'pending_inspection':
            return '#f59e0b'
        default:
            return '#64748b'
    }
}

const getAlertSeverity = (
    status: FinalMoveOutStatus,
): 'success' | 'warning' | 'info' | 'error' => {
    switch (status) {
        case 'final':
        case 'fmo_accepted':
            return 'success'
        case 'in_dispute':
            return 'error'
        case 'pending_inspection':
            return 'warning'
        default:
            return 'info'
    }
}

const getStatusIcon = (status: FinalMoveOutStatus) => {
    switch (status) {
        case 'in_dispute':
            return <Warning />
        case 'pending_inspection':
            return <Schedule />
        default:
            return <Info />
    }
}

const getMessageText = (
    status: FinalMoveOutStatus,
    endDate: string,
    moveOutState?: HubMoveOutState | null,
): string => {
    const moveOutDate = new Date(endDate).toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
    })

    if (status === 'inspection_complete' || moveOutState === 'complete') {
        return 'Inspection complete — review your results'
    }

    switch (status) {
        case 'not_started':
            return `Move-out scheduled for ${moveOutDate}`
        case 'pending_inspection':
            return 'Move-out inspection is pending'
        case 'fmo_sent':
        case 'fmo_reviewed':
            return 'Your final move-out statement is available for review'
        case 'fmo_accepted':
            return 'Final move-out statement accepted'
        case 'in_dispute':
            return 'Dispute in progress — review your move-out statement'
        case 'final':
            return 'Move-out finalized'
        default:
            return `Move-out scheduled for ${moveOutDate}`
    }
}

export const MoveOutCard: React.FC<MoveOutCardProps> = ({
    status,
    endDate,
    leaseId: _leaseId,
    moveOutState,
    fmoAvailable = false,
}) => {
    const theme = useTheme()
    const navigate = useNavigate()
    const statusColor = getStatusColor(
        moveOutState === 'complete' ? 'inspection_complete' : status,
    )
    const severity = getAlertSeverity(status)
    const buttonConfig = getButtonConfig(status, moveOutState, fmoAvailable)

    return (
        <Box
            sx={{
                background:
                    'linear-gradient(135deg, #2a2f35 0%, #1e2328 100%)',
                borderRadius: 3,
                padding: 3,
                marginBottom: 3,
                border: `1px solid ${theme.palette.grey[700]}`,
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Top accent bar */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${statusColor} 0%, ${theme.palette.primary.main} 100%)`,
                }}
            />

            {/* Header */}
            <FlexDiv
                style={{
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: theme.spacing(2),
                }}
            >
                <FlexDiv style={{ alignItems: 'center' }}>
                    <ExitToApp
                        style={{
                            color: statusColor,
                            marginRight: theme.spacing(1),
                            fontSize: 24,
                        }}
                    />
                    <Typography
                        variant="h6"
                        style={{
                            color: '#fff',
                            fontWeight: 600,
                        }}
                    >
                        Move-Out
                    </Typography>
                </FlexDiv>
                {/* <Chip
                    icon={getStatusIcon(status)}
                    label={getUiCamelCase(status)}
                    color={severity as 'success' | 'warning' | 'info' | 'error'}
                    size="small"
                    style={{
                        fontWeight: 600,
                        backgroundColor: statusColor,
                        color: 'white',
                    }}
                /> */}
            </FlexDiv>

            {/* Status message */}
            <Alert
                severity={severity}
                style={{
                    marginBottom: theme.spacing(2),
                    backgroundColor: 'rgba(255, 255, 255, 0.05)',
                    border: `1px solid ${statusColor}40`,
                    borderRadius: 2,
                }}
                icon={getStatusIcon(status)}
            >
                <Typography
                    variant="body2"
                    style={{
                        color: '#fff',
                        fontWeight: 500,
                    }}
                >
                    {getMessageText(status, endDate, moveOutState)}
                </Typography>
            </Alert>

            {/* Action button */}
            <Button
                variant="contained"
                fullWidth
                onClick={() => navigate(buttonConfig.route)}
                startIcon={buttonConfig.icon}
                sx={{
                    backgroundColor: statusColor,
                    color: 'white',
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: '1rem',
                    padding: theme.spacing(1.5, 2),
                    borderRadius: 2,
                    boxShadow: `0 4px 15px ${statusColor}40`,
                    '&:hover': {
                        backgroundColor: theme.palette.primary.dark,
                        boxShadow: `0 6px 20px ${statusColor}60`,
                    },
                }}
            >
                {buttonConfig.label}
            </Button>

            {/* Move-out date */}
            {endDate && (
                <Box
                    sx={{
                        marginTop: theme.spacing(2),
                        padding: theme.spacing(1.5),
                        backgroundColor: 'rgba(255, 255, 255, 0.05)',
                        borderRadius: 2,
                        border: `1px solid ${theme.palette.grey[600]}`,
                    }}
                >
                    <FlexDiv style={{ alignItems: 'center' }}>
                        <Schedule
                            style={{
                                color: '#8b949e',
                                marginRight: theme.spacing(1),
                                fontSize: 16,
                            }}
                        />
                        <Typography
                            variant="caption"
                            style={{
                                color: '#8b949e',
                                fontWeight: 500,
                            }}
                        >
                            Move-Out Date:{' '}
                            {new Date(endDate).toLocaleDateString('en-US', {
                                weekday: 'long',
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                            })}
                        </Typography>
                    </FlexDiv>
                </Box>
            )}
        </Box>
    )
}
